
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Product } from '@/types/product';
import { Loader2 } from 'lucide-react';

interface ProductDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentProduct: Product | null;
  setCurrentProduct: (product: Product | null) => void;
  selectedTags: string[];
  onTagSelect: (tagName: string) => void;
  groups: any[];
  tags: any[];
  onSave: () => Promise<void>;
  isSubmitting?: boolean;
}

const ProductDialog: React.FC<ProductDialogProps> = ({
  isOpen,
  setIsOpen,
  currentProduct,
  setCurrentProduct,
  selectedTags,
  onTagSelect,
  groups,
  tags,
  onSave,
  isSubmitting = false,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {currentProduct && currentProduct.id ? 'Edit Product' : 'Add Product'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={currentProduct?.title || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    title: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={currentProduct?.description || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    description: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="group">Group</Label>
            <Select
              value={currentProduct?.group_id || ''}
              onValueChange={(value) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    group_id: value || null
                  });
                }
              }}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  onClick={() => !isSubmitting && onTagSelect(tag.name)}
                  className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                    selectedTags.includes(tag.name) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="image">Image URL</Label>
            <Input
              id="image"
              value={currentProduct?.image || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    image: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="site_url">Site URL</Label>
            <Input
              id="site_url"
              value={currentProduct?.site_url || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    site_url: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="is_featured">Featured</Label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                checked={currentProduct?.is_featured || false}
                onChange={(e) => {
                  if (currentProduct) {
                    setCurrentProduct({
                      ...currentProduct,
                      is_featured: e.target.checked
                    });
                  }
                }}
                disabled={isSubmitting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-500">Show on homepage</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="rating">Rating (0-5)</Label>
            <Input
              id="rating"
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={currentProduct?.rating || 0}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    rating: parseFloat(e.target.value)
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
        </div>
        
        <DialogFooter className="sticky bottom-0 bg-background pt-2 pb-2">
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSubmitting || !currentProduct?.title}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDialog;
